import { useState, useRef, useEffect, KeyboardEvent } from 'react'
import ValidationMessage from './ValidationMessage'

interface EditableCellProps {
  value: number
  onSave: (value: number) => void
  onCancel?: () => void
  className?: string
  placeholder?: string
  disabled?: boolean
  formatValue?: (value: number) => string
  parseValue?: (value: string) => number
  isEditing?: boolean
  onEditingChange?: (editing: boolean) => void
  validate?: (value: number) => string | null
  allowNegative?: boolean
}

export default function EditableCell({
  value,
  onSave,
  onCancel,
  className = '',
  placeholder = '0',
  disabled = false,
  formatValue = (val) => val.toString(),
  parseValue = (str) => parseFloat(str) || 0,
  isEditing: controlledEditing,
  onEditingChange,
  validate,
  allowNegative = true
}: EditableCellProps) {
  const [internalEditing, setInternalEditing] = useState(false)
  const [inputValue, setInputValue] = useState('')
  const [error, setError] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  // Use controlled editing state if provided, otherwise use internal state
  const isEditing = controlledEditing !== undefined ? controlledEditing : internalEditing
  const setIsEditing = (editing: boolean) => {
    if (onEditingChange) {
      onEditingChange(editing)
    } else {
      setInternalEditing(editing)
    }
  }

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [isEditing])

  const handleStartEdit = () => {
    if (disabled) return
    setInputValue(value.toString())
    setError(null)
    setIsEditing(true)
  }

  const handleSave = () => {
    try {
      const numericValue = parseValue(inputValue)
      
      // Validate the parsed value
      if (isNaN(numericValue)) {
        setError('Please enter a valid number')
        return
      }

      // Check if negative values are allowed
      if (!allowNegative && numericValue < 0) {
        setError('Value cannot be negative')
        return
      }

      // Custom validation
      if (validate) {
        const validationError = validate(numericValue)
        if (validationError) {
          setError(validationError)
          return
        }
      }

      onSave(numericValue)
      setIsEditing(false)
      setError(null)
    } catch (err) {
      setError('Invalid value')
    }
  }

  const handleCancel = () => {
    setInputValue('')
    setError(null)
    setIsEditing(false)
    onCancel?.()
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleSave()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      handleCancel()
    }
  }

  const handleBlur = () => {
    // Small delay to allow click events on save/cancel buttons
    setTimeout(() => {
      if (isEditing) {
        handleSave()
      }
    }, 100)
  }

  if (isEditing) {
    return (
      <div className={`editable-cell editing ${className}`}>
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          placeholder={placeholder}
          className="editable-input"
          style={{
            background: 'rgba(0, 176, 255, 0.2)',
            border: '1px solid var(--primary-blue)',
            outline: 'none',
            padding: '4px 8px',
            borderRadius: '4px',
            color: 'var(--text-light)',
            fontFamily: 'Menlo, Consolas, monospace',
            textAlign: 'right',
            width: '100%',
            minWidth: '80px'
          }}
        />
        <ValidationMessage message={error} />
      </div>
    )
  }

  return (
    <div
      data-testid="editable-cell"
      className={`editable-cell ${disabled ? 'disabled' : 'clickable'} ${className}`}
      onClick={handleStartEdit}
      style={{
        cursor: disabled ? 'default' : 'pointer',
        padding: '4px 8px',
        borderRadius: '4px',
        transition: 'background-color 0.2s',
        fontFamily: 'Menlo, Consolas, monospace',
        textAlign: 'right',
        minHeight: '24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end'
      }}
      onMouseEnter={(e) => {
        if (!disabled) {
          e.currentTarget.style.backgroundColor = 'rgba(0, 176, 255, 0.1)'
        }
      }}
      onMouseLeave={(e) => {
        if (!disabled) {
          e.currentTarget.style.backgroundColor = 'transparent'
        }
      }}
    >
      {formatValue(value)}
    </div>
  )
}